"use client";

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import {
  ChevronDownIcon,
  type ChevronDownIconHandle,
  MessageSquareIcon,
  type MessageSquareIconHandle,
} from "@/components/widget/icons/animated";
import { UnreadBadge } from "@/components/widget/unread-badge";
import { pointerFocusHandlers } from "@/components/widget/use-pointer-focus";
import { ANCHOR_REFS } from "@/lib/launcher-anchors";
import { useIconHover } from "@/lib/use-icon-hover";
import { cn } from "@/lib/utils";

interface WidgetLauncherProps {
  isOpen: boolean;
  onToggle: () => void;
  /** Unread count surfaced as a pill on the FAB. Zero hides the
   *  badge entirely; 99+ caps to `99+` to keep the pill width bounded. */
  unreadCount?: number;
  /** Unread news. Shows a countless dot on the FAB ONLY when there's no
   *  unread-message count (the message count takes priority). */
  newsUnread?: boolean;
  /** Logo to show on the CLOSED launcher instead of the default chat-bubble
   *  icon — the (per-theme) header logo, passed by the shell only when the
   *  operator enabled `showLogoInLauncher`. `null`/absent → the default icon. */
  logoUrl?: string | null;
  /** Which bottom corner the FAB anchors to. Defaults to `"right"`. */
  position?: "left" | "right";
  /** Anchor to the host `<div>` (`absolute`) instead of the viewport
   *  (`fixed`). Set by the React-component embed — see `WidgetEnv.embedded`. */
  embedded?: boolean;
  /** While `true` (config fetch / auth handshake in flight) the FAB is inert: disabled and
   *  `aria-busy`, so a tap cannot double-fire the handshake. It looks exactly as it does when idle -
   *  no spinner, no icon swap, no size change. */
  loading?: boolean;
}

/**
 * Floating launcher. Theme-invariant brand surface (`--wx-launcher-bg`)
 * so the FAB reads as a wexio mark on any host page regardless of
 * light/dark preference.
 *
 * Icon morph between chat-bubble (closed) and X (open) via
 * `AnimatePresence`. Each lucide-animated icon's internal animation
 * fires when the **button** is hovered (not the icon itself) — wired
 * via `useIconHover` since the icon receives pointer events through
 * the button.
 */
export function WidgetLauncher({
  isOpen,
  onToggle,
  unreadCount = 0,
  newsUnread = false,
  logoUrl = null,
  position = "right",
  embedded = false,
  loading = false,
}: WidgetLauncherProps) {
  const t = useTranslations("launcher");
  const tNews = useTranslations("news");
  const messageHover = useIconHover<MessageSquareIconHandle>();
  const closeHover = useIconHover<ChevronDownIconHandle>();
  const activeHandlers = isOpen ? closeHover.parentHandlers : messageHover.parentHandlers;
  // Hide the badge while the panel is open — the visitor's already
  // looking at the chat. (UnreadBadge handles the 99+ cap itself.)
  // Launcher badge aggregates both signals in ONE top-right slot:
  //   - unread messages present → a number = messages + (1 if news unread)
  //   - only unread news (no messages) → a countless dot
  //   - nothing unread → hidden
  const launcherCount = unreadCount + (newsUnread ? 1 : 0);
  const showBadge = !isOpen && unreadCount > 0;
  const showNewsDot = !isOpen && unreadCount === 0 && newsUnread;

  return (
    <motion.button
      type="button"
      // Measurement anchor: the iframe shell reads this element's box (see
      // WidgetShell's closed-frame resize effect) to size the launcher frame
      // so the FAB + badge + shadow aren't clipped. Harmless on other embeds.
      data-wx-fab=""
      ref={ANCHOR_REFS.fab}
      onClick={loading ? undefined : onToggle}
      disabled={loading}
      aria-label={isOpen ? t("close") : t("open")}
      aria-busy={loading || undefined}
      whileHover={loading ? undefined : { scale: 1.05 }}
      whileTap={loading ? undefined : { scale: 0.92 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      // Drive the icon's hover animation from motion's POINTER-based hover
      // (not React's synthetic `onMouseEnter`). In the react-component's
      // Shadow DOM, synthetic mouse-enter is unreliable — the FAB still
      // scaled (motion's own hover) but the icon never wiggled. `onHoverStart`
      // / `onHoverEnd` are pointer-based and fire in a shadow root. The
      // spread below keeps `onFocus`/`onBlur` for keyboard parity.
      onHoverStart={activeHandlers.onMouseEnter}
      onHoverEnd={activeHandlers.onMouseLeave}
      {...activeHandlers}
      onPointerDown={pointerFocusHandlers.onPointerDown}
      onKeyDown={pointerFocusHandlers.onKeyDown}
      onBlur={(e) => {
        activeHandlers.onBlur();
        pointerFocusHandlers.onBlur(e);
      }}
      className={cn(
        embedded ? "absolute" : "fixed",
        // Max-int z-index puts the launcher above any host-page chrome
        // (sticky headers, full-page modals, cookie banners, …). Chat
        // widgets are conventionally the topmost element on the page;
        // anything that expects to live above is misconfigured host-side.
        "bottom-4 z-2147483600",
        position === "left" ? "left-4" : "right-4",
        "flex h-14 w-14 items-center justify-center rounded-full",
        "bg-wx-launcher-bg text-wx-launcher-fg",
        "shadow-[0_8px_24px_rgba(0,0,0,0.18)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-launcher-bg focus-visible:ring-offset-2 focus-visible:ring-offset-white",
        // Focus that came from a click is not keyboard focus: no ring (see use-pointer-focus).
        "data-wx-pointer:focus-visible:ring-0 data-wx-pointer:focus-visible:ring-offset-0",
      )}
      // Host offset (px) from the anchored corner — `--wx-off-*` are 0 by
      // default (no visual change) and set by the embed from `env.offset`.
      // Margin on a corner-anchored fixed element nudges it inward/up.
      style={
        position === "left"
          ? { marginBottom: "var(--wx-off-y,0px)", marginLeft: "var(--wx-off-x,0px)" }
          : { marginBottom: "var(--wx-off-y,0px)", marginRight: "var(--wx-off-x,0px)" }
      }
    >
      {/* aria-hidden: the count is a VISUAL indicator only. Left visible to the
          a11y tree it becomes text inside the button that its `aria-label`
          ("Open chat" / "Close") doesn't contain → a label/name mismatch. The
          messages tab carries its own announced badge. `contents` keeps the
          badge anchored to the button. */}
      <span aria-hidden className="contents">
        <UnreadBadge
          count={showBadge ? launcherCount : 0}
          dot={showNewsDot}
          label={showNewsDot ? tNews("unread") : undefined}
          size="md"
          className="absolute -top-1 -right-1 z-10"
        />
      </span>
      <AnimatePresence mode="wait" initial={false}>
        {isOpen ? (
          <motion.span
            key="x"
            initial={{ opacity: 0, rotate: -90, scale: 0.7 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0.7 }}
            transition={{ duration: 0.18 }}
          >
            <ChevronDownIcon ref={closeHover.iconRef} size={24} />
          </motion.span>
        ) : logoUrl ? (
          <motion.span
            key="logo"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.18 }}
            // Full-bleed: the logo fills the whole FAB (covers the launcher
            // background) rather than sitting as a small centred icon.
            className="absolute inset-0 overflow-hidden rounded-full"
          >
            {/* biome-ignore lint/performance/noImgElement: operator launcher logo of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
            <img src={logoUrl} alt="" className="h-full w-full object-cover" />
          </motion.span>
        ) : (
          <motion.span
            key="chat"
            initial={{ opacity: 0, rotate: 90, scale: 0.7 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: -90, scale: 0.7 }}
            transition={{ duration: 0.18 }}
          >
            <MessageSquareIcon
              ref={messageHover.iconRef}
              size={24}
              // `lucide-animated` icons render a wrapping <div>
              // (props extend HTMLAttributes<HTMLDivElement>), so
              // plain `fill-*` utilities land on the div and never
              // reach the inner SVG. Reach in with arbitrary
              // selectors AND override `stroke` too — these glyphs
              // are stroke-only by default, so without overriding
              // stroke you'd get a filled outline, not a true
              // silhouette.
              //
              // Both set to `currentColor` so the icon picks up
              // whatever color the launcher button has (driven by
              // `text-wx-launcher-fg` on the button + the operator's
              // `launcherForeground` theme token). Previously this
              // hard-coded `--wx-launcher-fg` / `--wx-bg` directly —
              // light mode worked but dark mode silently swapped to
              // `--wx-bg` which isn't the operator's foreground
              // token, breaking custom color overrides.
              className="[&_svg]:fill-current [&_svg]:stroke-current"
            />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
